import { Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDynamicFavicon } from './hooks/useDynamicFavicon';
import FaviconService from './utils/FaviconService';
import TitleService from './utils/TitleService';
import ErrorBoundary from './components/ErrorBoundary';
import StyleSheetManagerSetup from './components/StyleSheetManagerSetup';
import ScrollToTop from './components/common/ScrollToTop';
import NotFound from './pages/NotFound';
import GlobalStyle from './styles/GlobalStyle';
import { theme } from './styles/theme';
import Home from './pages/Home';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import Signup from './pages/Signup';
import SignupComplete from './pages/SignupComplete';
import ForgotPassword from './pages/forgot-password';
import ForgotPasswordVerify from './pages/forgot-password/verify';
import ForgotPasswordReset from './pages/forgot-password/reset';
import ForgotPasswordComplete from './pages/forgot-password/complete';
import TermsOfService from './pages/terms/TermsOfService';
import PrivacyPolicy from './pages/terms/PrivacyPolicy';
import MarketingConsent from './pages/terms/MarketingConsent';
import { ArchiveHome, ArchiveDetail } from './pages/archive';
import { NewsletterHome } from './pages/newsletter';
import ClassIndex from './pages/class/ClassIndex';
import ClassCategory from './pages/class/ClassCategory';
import ClassLecture from './pages/class/ClassLecture';
import StreamingIndex from './pages/class/streaming/StreamingIndex';
import TestMaterials from './pages/test/TestMaterials';
import BasicUiTest from './pages/test/BasicUiTest';
import CommunityHome from './pages/community/CommunityHome';
import { PostDetail } from './pages/community';
import LectureQuestions from './pages/community/categories/LectureQuestions';
import Study from './pages/community/categories/Study';
import InfoSharing from './pages/community/categories/InfoSharing';
import Notice from './pages/community/categories/Notice';
import WritePost from './pages/WritePost';
import CoffeeChatHome from './pages/coffee-chat/CoffeeChatHome';
import CoffeeChatDetail from './pages/coffee-chat/CoffeeChatDetail';
import CoffeeChatWrite from './pages/coffee-chat/CoffeeChatWrite';
import SearchIndex from './pages/search';
import MyPageIndex from './pages/mypage/MyPageIndex';
import Survey from './pages/mypage/Survey';
import { 
  AdminIndex,
  Dashboard, 
  HomeManagement, 
  HomeSetting,
  UserManagement, 
  UserQualification,
  ClassManagement, 
  LectureManagement, 
  LectureRegister,
  CoffeeChatManagement, 
  MentorManagement, 
  MentorRegister, 
  QuestionManagement,
  PostManagement,
  AdminPostManagement,
  LectureQAManagement,
  CommentManagement,
  StudyManagement,
  AdminWrite,
  NewsletterManagement,
  NewsletterRegister,
  InquiryList
} from './pages/admin';
import AccountCreate from './pages/admin/account/AccountCreate';
import AdminLayout from './components/admin/layout/AdminLayout';
import { RootState } from './store';
import { useAppDispatch } from './hooks/useAppDispatch';
import { logout } from './store/slices/authSlice';
import { setupAutoLogout, isValidToken } from './utils/sessionUtils';
import { refreshUserInfo } from './hooks/useUserInfo';
import { initializeAuth } from './services/auth/authApi';
import { basicUiApi } from './services/basicUiApi';
import { updateFavicon, updateWebTitle } from './utils/uiUtils';
import { getActivePopups, PopupBanner } from './services/admin/popupApi';
import LoginPopupModal, { shouldShowPopup } from './components/modals/LoginPopupModal';

// 로컬에서 타이틀 생성하는 함수 (API 호출 없이)
const generateLocalTitle = (params: any): string => {
  let title = params.baseName || 'Campus';
  
  if (params.pageName) {
    title += ` | ${params.pageName}`;
  }
  
  if (params.notificationCount && params.notificationCount > 0) {
    title = `(${params.notificationCount}) ${title}`;
  }
  
  if (params.userName) {
    title += ` | ${params.userName}`;
  }
  
  return title;
};

function App() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth);
  
  const [popupBanners, setPopupBanners] = useState<PopupBanner[]>([]);
  const location = useLocation();

  useEffect(() => {
    // 팝업은 홈(/)에서만, 로그인 여부와 무관하게 브라우저(localStorage) 기준으로 노출
    if (location.pathname !== '/') return;
    if (!shouldShowPopup()) return;

    getActivePopups()
      .then((list) => { if (list.length > 0) setPopupBanners(list); })
      .catch(() => {});
  }, [location.pathname]);

  // 파비콘 관리 훅
  const { setFavicon, setNotificationFavicon } = useDynamicFavicon();
  
  // 타이틀 관리는 로컬 함수로만 처리 (API 호출 방지)
  
  // 파비콘 서비스 인스턴스
  const faviconService = FaviconService.getInstance();
  
  // 타이틀 서비스 인스턴스
  const titleService = TitleService.getInstance();

  useEffect(() => {
    // 앱 시작 시 토큰 갱신 및 인증 초기화
    const initAuth = async () => {
      try {
        const isAuthInitialized = await initializeAuth();
        
        if (isAuthInitialized) {
          // 토큰 갱신 성공 시 사용자 프로필 가져오기
          /// console.log('App.tsx - 토큰 갱신 성공, 사용자 프로필을 가져옵니다.');
          // dispatch(fetchUserProfile());
          // useUserInfo 훅의 사용자 정보도 동시에 로드
          refreshUserInfo().catch((error) => {
            /// console.error('App.tsx - Failed to refresh user info:', error);
          });
        } else {
          // 토큰 갱신 실패 시 로그아웃 처리
          /// console.log('App.tsx - 토큰 갱신 실패, 비로그인 상태로 설정합니다.');
          dispatch(logout());
        }
      } catch (error) {
        /// console.error('App.tsx - 인증 초기화 에러:', error);
        dispatch(logout());
      }
    };

    // 기존 로직과 함께 실행
    const token = localStorage.getItem('access_token');
    
    if (token && !isValidToken()) {
      // 만료된 토큰이면 토큰 갱신 시도
      /// console.log('App.tsx - 토큰이 만료되어 갱신을 시도합니다.');
      initAuth();
    } else if (token && !isAuthenticated) {
      // 유효한 토큰이 있으면 사용자 프로필 가져오기
      /// console.log('App.tsx - 유효한 토큰으로 사용자 프로필을 가져옵니다.');
      // dispatch(fetchUserProfile());
      // useUserInfo 훅의 사용자 정보도 동시에 로드
      refreshUserInfo().catch((error) => {
        /// console.error('App.tsx - Failed to refresh user info:', error);
      });
    } else if (!token) {
      // 토큰이 없으면 토큰 갱신 시도 (쿠키에 리프레시 토큰이 있을 수 있음)
      /// console.log('App.tsx - 액세스 토큰이 없어 리프레시 토큰으로 갱신을 시도합니다.');
      initAuth();
    } else if (token && isAuthenticated) {
      // 이미 인증된 상태에서 새로고침한 경우 사용자 정보만 확인
      // /// console.log('App.tsx - 이미 인증된 상태, 사용자 정보 새로고침');
      refreshUserInfo().catch((error) => {
        /// console.error('App.tsx - Failed to refresh user info on refresh:', error);
      });
    }
  }, [dispatch, isAuthenticated]); // token 의존성 제거하여 중복 호출 방지

  // 자동 로그아웃 타이머 제거 - 강의 시청 중 토큰 갱신으로 대체
  // useEffect(() => {
  //   // 로그인된 상태에서 자동 로그아웃 타이머 설정
  //   if (isAuthenticated && token) {
  //     const clearTimer = setupAutoLogout(() => {
  //       dispatch(logout());
  //     });
  //     
  //     return clearTimer; // 컴포넌트 언마운트 시 타이머 정리
  //   }
  // }, [dispatch, isAuthenticated, token]);

  // 기본 UI 설정 로드
  useEffect(() => {
    const loadBasicUiSettings = async () => {
      try {
        const uiData = await basicUiApi.getBasicUi();
        
        // uiData가 null인 경우 (404 에러 등) 기본값 사용
        if (!uiData) {
          /// console.warn('기본 UI 설정이 없습니다. 기본값을 사용합니다.');
          updateWebTitle('Campus');
          return;
        }
        
        updateWebTitle(uiData.web_name);
        
        // 타이틀 서비스 설정도 업데이트
        const currentTitleConfig = titleService.getCurrentConfig();
        const newTitleConfig = {
          ...currentTitleConfig,
          baseName: uiData.web_name
        };
        titleService.setCurrentConfig(newTitleConfig);
        
        // 파비콘 업데이트 (독립적으로 처리, 실패해도 타이틀에 영향 없음)
        try {
          await updateFavicon(uiData.favicon_url);
        } catch (faviconError) {
          /// console.error('파비콘 업데이트 실패 (타이틀은 유지됨):', faviconError);
        }
        
      } catch (error) {
        /// console.error('기본 UI 설정 로드 실패:', error);
        // API 실패 시 기본값으로 타이틀만 설정
        updateWebTitle('Campus');
      }
    };

    // 다른 파비콘 설정보다 먼저 실행되도록 타이머 사용
    setTimeout(loadBasicUiSettings, 100);
  }, [titleService]);

  // 파비콘 관리 useEffect (기본 UI API 파비콘이 설정된 후에만 실행)
  useEffect(() => {
    // 테마 변경 감지만 유지
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = (e: MediaQueryListEvent) => {
      // 기본 UI 파비콘을 유지하기 위해 테마 변경 시에도 파비콘 변경 안함
    };

    mediaQuery.addEventListener('change', handleThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleThemeChange);
    };
  }, [setFavicon, faviconService]);

  // 알림 개수 변경 감지 (예시)
  useEffect(() => {
    // 실제 알림 개수는 해당 상태나 API에서 가져와야 합니다
    // 여기서는 예시로 localStorage를 사용
    const notificationCount = parseInt(localStorage.getItem('notification_count') || '0', 10);
    
    if (notificationCount > 0) {
      const currentConfig = faviconService.getCurrentConfig();
      setNotificationFavicon(notificationCount, currentConfig);
    }
  }, [setNotificationFavicon, faviconService]);

  // 타이틀 관리 useEffect (기본 UI API에서 로드된 후 설정됨)
  useEffect(() => {
    const userId = localStorage.getItem('user_id') || undefined;
    const userName = localStorage.getItem('user_name') || undefined;
    
    const initialTitleConfig = {
      baseName: 'Campus', // 기본값, 기본 UI API에서 로드되면 업데이트됨
      userId: userId,
      userName: userName,
      // pageName은 라우터에서 설정
      // company: 'campus',
    };

    // 타이틀 서비스 설정만 저장 (실제 타이틀 변경은 기본 UI API에서 처리)
    titleService.setCurrentConfig(initialTitleConfig);
    // 타이틀 설정 변경 이벤트 리스너 (페이지 변경 등에서만 사용)
    const handleTitleConfigChange = (event: CustomEvent) => {
      const params = event.detail;
      // 기본 UI API에서 설정된 baseName이 있다면 그것을 우선 사용
      const currentConfig = titleService.getCurrentConfig();
      const finalParams = {
        ...params,
        baseName: currentConfig.baseName || params.baseName || 'Campus'
      };
      
      const localTitle = generateLocalTitle(finalParams);
      document.title = localTitle;
    };

    window.addEventListener('titleConfigChanged', handleTitleConfigChange as EventListener);

    return () => {
      window.removeEventListener('titleConfigChanged', handleTitleConfigChange as EventListener);
    };
  }, [titleService]);

  // 알림 개수 변경 감지 (파비콘과 타이틀 동시 업데이트)
  useEffect(() => {
    const notificationCount = parseInt(localStorage.getItem('notification_count') || '0', 10);
    
    if (notificationCount > 0) {
      const currentFaviconConfig = faviconService.getCurrentConfig();
      const currentTitleConfig = titleService.getCurrentConfig();
      
      setNotificationFavicon(notificationCount, currentFaviconConfig);
      
      // 알림 개수 포함해서 타이틀 업데이트 (API 호출 없이)
      const titleWithNotification = generateLocalTitle({
        ...currentTitleConfig,
        notificationCount
      });
      document.title = titleWithNotification;
    }
  }, [setNotificationFavicon, faviconService, titleService]);

  return (
    <StyleSheetManagerSetup>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <ErrorBoundary>
          <ScrollToTop />
          <Routes>
          {/* 홈 */}
          <Route path="/" element={
            <ErrorBoundary>
              <Home />
              {/* <Empty /> */}
              {/* <NotFound /> */}
            </ErrorBoundary>
          } />
          
          {/* 인증 */}
          <Route path="/login" element={
            <ErrorBoundary>
              <Login />
              {/* <NotFound /> */}
            </ErrorBoundary>
          } />
          <Route path="/admin/login" element={
            <ErrorBoundary>
              <AdminLogin />
            </ErrorBoundary>
          } />
          <Route path="/signup" element={
            <ErrorBoundary>
              <Signup />
              {/* <NotFound /> */}
            </ErrorBoundary>
          } />
          <Route path="/signup-complete" element={
            <ErrorBoundary>
              <SignupComplete />
            </ErrorBoundary>
          } />
          <Route path="/forgot-password" element={
            <ErrorBoundary>
              <ForgotPassword />
            </ErrorBoundary>
          } />
          <Route path="/forgot-password/verify" element={
            <ErrorBoundary>
              <ForgotPasswordVerify />
            </ErrorBoundary>
          } />
          <Route path="/forgot-password/reset" element={
            <ErrorBoundary>
              <ForgotPasswordReset />
            </ErrorBoundary>
          } />
          <Route path="/forgot-password/complete" element={
            <ErrorBoundary>
              <ForgotPasswordComplete />
            </ErrorBoundary>
          } />
          
          {/* 약관 */}
          <Route path="/terms-of-service" element={
            <ErrorBoundary>
              <TermsOfService />
            </ErrorBoundary>
          } />
          <Route path="/privacy-policy" element={
            <ErrorBoundary>
              <PrivacyPolicy />
            </ErrorBoundary>
          } />
          <Route path="/marketing-consent" element={
            <ErrorBoundary>
              <MarketingConsent />
            </ErrorBoundary>
          } />
          
          {/* 클래스 */}
          <Route path="/class" element={
            <ErrorBoundary>
              <ClassIndex />
            </ErrorBoundary>
          } />
          <Route path="/class/category" element={
            <ErrorBoundary>
              <ClassCategory />
            </ErrorBoundary>
          } />
          <Route path="/class/lecture" element={
            <ErrorBoundary>
              <ClassLecture />
            </ErrorBoundary>
          } />
          <Route path="/class/lecture/:id" element={
            <ErrorBoundary>
              <ClassLecture />
            </ErrorBoundary>
          } />
          <Route path="/class/streaming/:lectureId" element={
            <ErrorBoundary>
              <StreamingIndex />
            </ErrorBoundary>
          } />
          <Route path="/test/materials" element={
            <ErrorBoundary>
              <TestMaterials />
            </ErrorBoundary>
          } />
          <Route path="/test/basic-ui" element={
            <ErrorBoundary>
              <BasicUiTest />
            </ErrorBoundary>
          } />
          <Route path="/classes" element={
            <ErrorBoundary>
              {/* <ClassIndex /> */}
              <NotFound />
            </ErrorBoundary>
          } />
          <Route path="/classes/detail/:id" element={
            <ErrorBoundary>
              {/* <ClassDetail /> */}
              <NotFound />
            </ErrorBoundary>
          } />
          <Route path="/classes/classroom/:id" element={
            <ErrorBoundary>
              {/* <ClassRoom /> */}
              <NotFound />
            </ErrorBoundary>
          } />
          <Route path="/classes/:category" element={
            <ErrorBoundary>
              <NotFound />
            </ErrorBoundary>
          } />
          
          {/* 커피챗 */}
          <Route path="/coffee-chat" element={
            <ErrorBoundary>
              <CoffeeChatHome />
            </ErrorBoundary>
          } />
          <Route path="/coffee-chat/write/:lecturerId" element={
            <ErrorBoundary>
              <CoffeeChatWrite />
            </ErrorBoundary>
          } />
          <Route path="/coffee-chat/post/:postId" element={
            <ErrorBoundary>
              <PostDetail />
            </ErrorBoundary>
          } />
          <Route path="/coffee-chat/:id" element={
            <ErrorBoundary>
              <CoffeeChatDetail />
            </ErrorBoundary>
          } />
          <Route path="/coffee-chat/:category" element={
            <ErrorBoundary>
              <NotFound />
            </ErrorBoundary>
          } />
          
          {/* 아카이브 */}
          <Route path="/archive" element={
            <ErrorBoundary>
              <ArchiveHome />
            </ErrorBoundary>
          } />
          <Route path="/archive/:id" element={
            <ErrorBoundary>
              <ArchiveDetail />
            </ErrorBoundary>
          } />
          <Route path="/archive/:category" element={
            <ErrorBoundary>
              <NotFound />
            </ErrorBoundary>
          } />
          
          {/* 뉴스레터 */}
          <Route path="/newsletter" element={
            <ErrorBoundary>
              <NewsletterHome />
            </ErrorBoundary>
          } />
          
          {/* 검색 */}
          <Route path="/search" element={
            <ErrorBoundary>
              <SearchIndex />
            </ErrorBoundary>
          } />

          {/* 마이페이지 */}
          <Route path="/mypage/inquiry/post/:postId" element={
            <ErrorBoundary>
              <PostDetail />
            </ErrorBoundary>
          } />
          <Route path="/mypage/inquiry/write" element={
            <ErrorBoundary>
              <WritePost />
            </ErrorBoundary>
          } />
          <Route path="/mypage/survey/:lectureId" element={
            <ErrorBoundary>
              <Survey />
            </ErrorBoundary>
          } />
          <Route path="/mypage/*" element={
            <ErrorBoundary>
              <MyPageIndex />
            </ErrorBoundary>
          } />
          
          {/* 커뮤니티 */}
          <Route path="/community" element={
            <ErrorBoundary>
              <CommunityHome />
            </ErrorBoundary>
          } />
          <Route path="/community/post/:postId" element={
            <ErrorBoundary>
              <PostDetail />
            </ErrorBoundary>
          } />
          <Route path="/community/lecture" element={
            <ErrorBoundary>
              <LectureQuestions />
            </ErrorBoundary>
          } />
          <Route path="/community/lecture-questions" element={
            <ErrorBoundary>
              <LectureQuestions />
            </ErrorBoundary>
          } />
          <Route path="/community/study" element={
            <ErrorBoundary>
              <Study />
            </ErrorBoundary>
          } />
          <Route path="/community/information" element={
            <ErrorBoundary>
              <InfoSharing />
            </ErrorBoundary>
          } />
          <Route path="/community/info-sharing" element={
            <ErrorBoundary>
              <InfoSharing />
            </ErrorBoundary>
          } />
          <Route path="/community/notice" element={
            <ErrorBoundary>
              <Notice />
            </ErrorBoundary>
          } />
          <Route path="/community/write" element={
            <ErrorBoundary>
              <WritePost />
            </ErrorBoundary>
          } />
          <Route path="/community/:category" element={
            <ErrorBoundary>
              <NotFound />
            </ErrorBoundary>
          } />
          
          {/* 관리자 - 중첩 라우터 */}
          <Route path="/admin/*" element={
            <ErrorBoundary>
              <AdminLayout />
            </ErrorBoundary>
          }>
            {/* Admin Index - Redirect to Dashboard */}
            <Route index element={<AdminIndex />} />
            
            {/* Dashboard Section */}
            <Route path="dashboard" element={<Dashboard />} />
            
            {/* Home Section */}
            <Route path="home" element={<HomeManagement />} />
            <Route path="home/setting" element={<HomeSetting />} />
            
            {/* User Section */}
            <Route path="user" element={<UserManagement />} />
            <Route path="user/create" element={<AccountCreate />} />
            <Route path="user/qualification" element={<UserQualification />} />
            
            {/* Class Section */}
            <Route path="class" element={<ClassManagement />} />
            <Route path="class/lecture" element={<LectureManagement />} />
            <Route path="class/lecture/register" element={<LectureRegister />} />
            
            {/* Coffee Chat Section */}
            <Route path="coffee-chat" element={<CoffeeChatManagement />} />
            <Route path="coffee-chat/mentor" element={<MentorManagement />} />
            <Route path="coffee-chat/mentor-register" element={<MentorRegister />} />
            <Route path="coffee-chat/mentor-edit" element={<MentorRegister />} />
            <Route path="coffee-chat/question" element={<QuestionManagement />} />

            {/* Community Section */}
            <Route path="community/post" element={<PostManagement />} />
            <Route path="community/admin-post" element={<AdminPostManagement />} />
            <Route path="community/lecture-qa" element={<LectureQAManagement />} />
            <Route path="community/comment" element={<CommentManagement />} />
            <Route path="community/study" element={<StudyManagement />} />
            <Route path="community/write" element={<AdminWrite />} />
            
            {/* Newsletter Section */}
            <Route path="newsletter" element={<NewsletterManagement />} />
            <Route path="newsletter/register" element={<NewsletterRegister />} />

            {/* Inquiry Section */}
            <Route path="inquiry/list" element={<InquiryList />} />
          </Route>
          
          {/* 사용자 */}
          <Route path="/dashboard" element={
            <ErrorBoundary>
              {/* <Dashboard /> */}
              <NotFound />
            </ErrorBoundary>
          } />
          
          {/* 변경 됨. not found 화면 */}
          <Route path="*" element={
            <ErrorBoundary>
              <NotFound />
            </ErrorBoundary>
          } />
        </Routes>
          {popupBanners.length > 0 && (
            <LoginPopupModal
              popups={popupBanners}
              onClose={() => setPopupBanners([])}
            />
          )}
      </ErrorBoundary>
    </ThemeProvider>
  </StyleSheetManagerSetup>
  );
}

export default App;
